import { z } from 'zod';
import type { components } from './schema.js';
import { montarCaminho, requisitar } from './client.js';
import type { Pagina, ParametrosDePagina } from './paginacao.js';
import { paginaSchema, parametrosDePagina } from './paginacao.js';
import { analisar } from './validacao.js';

/**
 * Depósitos do ERP, por filial (RF-083).
 *
 * O local de toda operação é o par filial + depósito do Senior (`codFil` +
 * `codDep`). A lista é SÓ LEITURA no painel: ela vem do ERP pela
 * sincronização, e é dela que o coletor baixa os depósitos que aceita.
 */

const CAMINHO_COLECAO = montarCaminho('/depositos');
const CAMINHO_RESERVAS = montarCaminho('/inventarios/reservas');

export const depositoSchema = z.object({
  id: z.string(),
  codFil: z.number().int(),
  /** Forma CANÔNICA (NFKC, aparado, caixa alta) — a que o coletor envia. */
  codDep: z.string(),
  /** O código como o ERP o guarda. */
  codDepNoErp: z.string(),
  desDep: z.string(),
  abrDep: z.string(),
  ativo: z.boolean(),
  sincronizadoEm: z.string(),
}) satisfies z.ZodType<components['schemas']['DepositoResponse']>;

export type Deposito = z.infer<typeof depositoSchema>;

const paginaDeDepositosSchema = paginaSchema(depositoSchema);

const importacaoDoErpSchema = z.object({
  resumo: z.object({
    novos: z.number().int().nonnegative(),
    atualizados: z.number().int().nonnegative(),
    inativados: z.number().int().nonnegative(),
    inalterados: z.number().int().nonnegative(),
  }),
  descartados: z.array(z.object({ codDep: z.string(), motivo: z.string() })),
  filiaisComFalha: z.array(
    z.object({ codFil: z.number().int(), classe: z.string(), motivo: z.string() }),
  ),
  avisos: z.array(z.string()),
});

/** O que "Sincronizar do ERP" devolve: contagens e o que não pôde entrar, com o motivo. */
export type ImportacaoDoErp = z.infer<typeof importacaoDoErpSchema>;

export const reservaSchema = z.object({
  id: z.string(),
  codFil: z.number().int(),
  depositoErp: z.string(),
  reservadoEm: z.string(),
  dispositivoId: z.string(),
  operador: z.object({ id: z.string(), matricula: z.string(), nome: z.string() }),
}) satisfies z.ZodType<components['schemas']['ReservaInventarioResponse']>;

/** Um depósito travado para contagem: só uma pessoa conta o mesmo depósito por vez. */
export type ReservaDeContagem = z.infer<typeof reservaSchema>;

const paginaDeReservasSchema = paginaSchema(reservaSchema);

export type SituacaoDeposito = 'Ativo' | 'Inativo';

export type FiltroDeDepositos = ParametrosDePagina & {
  /** Trecho de código, nome ou abreviatura, sem depender da caixa. */
  readonly busca?: string;
  readonly situacao?: SituacaoDeposito;
  readonly codFil?: number;
};

export type FiltroDeReservas = ParametrosDePagina & { readonly codFil?: number };

export async function listarDepositos(filtro: FiltroDeDepositos = {}): Promise<Pagina<Deposito>> {
  const corpo = await requisitar(CAMINHO_COLECAO, {
    consulta: {
      ...parametrosDePagina(filtro),
      busca: filtro.busca,
      situacao: filtro.situacao,
      codFil: filtro.codFil,
    },
  });

  return analisar(paginaDeDepositosSchema, corpo, CAMINHO_COLECAO);
}

/**
 * Teto da sincronização — bem acima dos 20 s do resto do painel.
 *
 * O servidor espera até o tempo limite da integração (30 s por padrão) a CADA
 * página de cada filial, e a primeira chamada do dia ao Senior já passou disso.
 * Cortar antes mostraria erro enquanto o servidor ainda termina de gravar.
 */
export const TEMPO_LIMITE_DA_IMPORTACAO_DO_ERP_MS = 180_000;

/**
 * Traz os depósitos de todas as filiais do ERP. Só leitura no Senior.
 *
 * Filial que falhou não é erro: volta em `filiaisComFalha`, e os depósitos
 * dela ficam como estavam.
 */
export async function importarDoErp(): Promise<ImportacaoDoErp> {
  const caminho = montarCaminho('/depositos/importar-do-erp');
  const corpo = await requisitar(caminho, {
    metodo: 'POST',
    ...(typeof AbortSignal.timeout === 'function'
      ? { sinal: AbortSignal.timeout(TEMPO_LIMITE_DA_IMPORTACAO_DO_ERP_MS) }
      : {}),
  });
  return analisar(importacaoDoErpSchema, corpo, caminho);
}

/** Reservas de contagem vigentes — quem está contando qual depósito agora. */
export async function listarReservas(
  filtro: FiltroDeReservas = {},
): Promise<Pagina<ReservaDeContagem>> {
  const corpo = await requisitar(CAMINHO_RESERVAS, {
    consulta: { ...parametrosDePagina(filtro), codFil: filtro.codFil },
  });

  return analisar(paginaDeReservasSchema, corpo, CAMINHO_RESERVAS);
}

/**
 * Libera uma reserva presa (aparelho perdido, operador que foi embora).
 *
 * Não apaga contagem nenhuma: só destrava o depósito para outra pessoa contar.
 */
export async function liberarReserva(id: string): Promise<void> {
  const caminho = montarCaminho('/inventarios/reservas/{id}', { id });
  await requisitar(caminho, { metodo: 'DELETE' });
}
